import React from 'react';
import { Tooltip } from '@prisjakt/ui/tooltip';
import { Direction } from '@prisjakt/ui';
import { useDispatch, useSelector } from 'react-redux';

import { useAppContext } from 'src/components/app-context';
import {
  filterQuery,
  filterChangeAction,
  compareProperty,
} from '../../../../actions/filter';
import isNumeric from '../../../../../../globals/helpers/is-numeric';
import { Filter } from '../filter';
import ErrorBoundary from '../../../../../../components/error-boundary';

import * as S from './styles';
import type { AppState } from 'src/reducers';
import type { Property } from '../../types';
import { IconChevronDown } from '@prisjakt/ui/icon/chevron-down';
import { IconChevronUp } from '@prisjakt/ui/icon/chevron-up';
import { IconEye } from '@prisjakt/ui/icon/eye';
import { IconTrash } from '@prisjakt/ui/icon/trash';

function getListItemLimit({ type }: Property) {
  switch (type) {
    case 'list-tree-and':
    case 'list-tree-or': {
      return 4;
    }
    case 'list-or':
    case 'list-and':
    case 'list-or-reduced':
    case 'list-and-reduced': {
      return 5;
    }
    default: {
      return undefined;
    }
  }
}

interface FilterPanelProps {
  count?: number;
  isBrandFilteredCategory?: boolean;
  displayFilter: Property;
  canCompare?: boolean;
  filteredValues: Record<string, any>;
  hideName?: boolean;
  layout?: 'horizontal' | 'vertical';
}

const FilterPanel: React.FC<
  React.PropsWithChildren<React.PropsWithChildren<FilterPanelProps>>
> = props => {
  const [showAllValues, setShowAllValues] = React.useState({});
  const categoryId = useSelector(
    (state: AppState) => state.areas.category.filter.categoryId as string
  );
  const comparing = useSelector(
    (state: AppState) => state.areas.category.filter.comparing as string[]
  );
  const query = useSelector(
    (state: AppState) =>
      state.areas.category.filter.query as Record<string, any>
  );
  const dispatch = useDispatch();

  function handleToggleValues(id: string) {
    setShowAllValues(prevState => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  }

  const {
    filteredValues,
    isBrandFilteredCategory,
    hideName,
    canCompare = true,
    layout = 'vertical',
    displayFilter,
  } = props;

  const { id, name, children, unit } = displayFilter;
  const { t } = useAppContext();
  const values = filteredValues[id] && filteredValues[id].values;

  // Do not render filters with no values
  if (values && values.length === 0) {
    return null;
  }

  // Never render the Brand-filter if we're in a Brand Filtered Category
  if (isBrandFilteredCategory && id === 'brands') {
    return null;
  }

  const limitValues = getListItemLimit(displayFilter);
  const horizontal = layout === 'horizontal';

  return (
    <ErrorBoundary onError={() => null}>
      <S.PanelWrapper
        hideLine={hideName || !!children}
        horizontal={horizontal}
        role="group"
        aria-labelledby={`filter-panel-title-${id}`}
        data-test="FilterPanel"
        data-test-title={id}
      >
        <S.PanelInner horizontal={horizontal}>
          {!hideName && (
            <S.PanelHeader horizontal={horizontal}>
              <S.PanelTitleWrapper id={`filter-panel-title-${id}`}>
                <S.PanelTitle>{name}</S.PanelTitle>
                {unit && unit.length > 0 && (
                  <S.PanelTitleUnit>({unit})</S.PanelTitleUnit>
                )}
              </S.PanelTitleWrapper>

              {query[id] !== undefined && (
                <S.ClearFilterTooltip
                  placement={Direction.Left}
                  title={t('filter', 'button.clear-filter')}
                >
                  <S.PanelIcon
                    type="button"
                    aria-label={t('filter', 'button.clear-filter')}
                    onClick={() => {
                      const newQuery = { ...query };
                      delete newQuery[id];
                      dispatch(filterQuery(newQuery));
                      dispatch(filterChangeAction(categoryId, newQuery));
                    }}
                  >
                    <IconTrash color="maybeBlue700" />
                  </S.PanelIcon>
                </S.ClearFilterTooltip>
              )}
              {isNumeric(id) && canCompare && (
                <S.ToggleColumnTooltip
                  placement={Direction.Left}
                  title={t('filter', 'properties.toggle-column')}
                >
                  <S.PanelCompareIcon
                    selected={comparing.includes(id)}
                    type="button"
                    aria-label={t('filter', 'properties.toggle-column')}
                    aria-pressed={comparing.includes(id)}
                    onClick={() => {
                      dispatch(compareProperty(id));
                    }}
                  >
                    <IconEye color="maybeBlue700" />
                  </S.PanelCompareIcon>
                </S.ToggleColumnTooltip>
              )}
              {horizontal && (
                <S.PanelIcon
                  hidden={
                    !values || !limitValues || limitValues >= values.length
                  }
                  type="button"
                  aria-label={
                    showAllValues[id]
                      ? t('filter', 'properties.quantity-show-less')
                      : t('filter', 'properties.quantity-show-more')
                  }
                  onClick={() => {
                    handleToggleValues(id);
                  }}
                  data-test="MoreFiltersToggleButton"
                >
                  <Tooltip
                    placement={Direction.Up}
                    title={
                      showAllValues[id]
                        ? t('filter', 'properties.quantity-show-less')
                        : t('filter', 'properties.quantity-show-more')
                    }
                  >
                    {showAllValues[id] ? (
                      <IconChevronUp color="maybeBlue700" />
                    ) : (
                      <IconChevronDown color="maybeBlue700" />
                    )}
                  </Tooltip>
                </S.PanelIcon>
              )}
            </S.PanelHeader>
          )}
          <S.PanelFilter>
            <Filter
              layout={layout}
              displayFilter={displayFilter}
              query={query}
              categoryId={categoryId}
              limitValues={limitValues}
              showAllValues={Boolean(showAllValues[id])}
              onToggleAllValues={panelId => {
                handleToggleValues(panelId);
              }}
              filteredValues={filteredValues}
            />
          </S.PanelFilter>
        </S.PanelInner>
        {children &&
          children.map(filter => {
            if (
              filter.type === 'boolean' ||
              filter.type === 'string' ||
              filter.children ||
              (filteredValues[filter.id] && filteredValues[filter.id].values)
            ) {
              return (
                <FilterPanel
                  key={filter.id}
                  layout={layout}
                  displayFilter={filter}
                  filteredValues={filteredValues}
                />
              );
            }
            return null;
          })}
      </S.PanelWrapper>
    </ErrorBoundary>
  );
};

export default FilterPanel;
